/**
 * Dopuszczalne masy na osiach dla Iveco Tourist Line (Sekcja 16.2 / 17.2 -
 * Technically permissible maximum mass on each axle). Wartości dla 16.2
 * (maxMassAxle1/maxMassAxle2) i 17.2 (intendedMaxMassAxle1/intendedMaxMassAxle2)
 * są identyczne - to ten sam wynik wpisywany w oba miejsca.
 *
 * Klucz zależy od:
 * - tonażu ({{versionTonnage}}, np. "72", "82", "8X"),
 * - oznaczenia skrzyni/napędu zakodowanego w 3. i 4. znaku {{baseVehicleVersion}}
 *   (np. "DN**AM**A1FX75" -> "AM"; wartość ta jest edytowalna per dokument,
 *   więc nie zgadujemy jej znaczenia - czytamy dosłownie te dwa znaki).
 *
 * Typ AxleMassLimit oraz uogólniona funkcja odczytująca dane (getAxleMassLimit,
 * per zabudowa) mieszkają w vehicle-axle-mass-limits.ts (ten sam folder) -
 * ten plik zawiera wyłącznie dane i logikę specyficzną dla Iveco.
 *
 * @module iveco-axle-mass-limits
 */

import type { AxleMassLimit } from "./vehicle-axle-mass-limits";

export const IVECO_AXLE_MASS_LIMITS: Record<
  string,
  Record<string, AxleMassLimit>
> = {
  "72": {
    "7M": { axle1: 2500, axle2: 5350 },
    AM: { axle1: 2700, axle2: 5350 },
  },
  "82": {
    AM: { axle1: 2700, axle2: 5600 },
  },
  // Tonaż z pierwszą cyfrą "8" inny niż "82" (np. 80, 81, 83-89)
  "8X": {
    AM: { axle1: 2700, axle2: 5800 },
  },
};

/**
 * Wyciąga oznaczenie skrzyni/napędu z 3. i 4. znaku wartości {{baseVehicleVersion}}
 * (np. "DNAMA1FX75" -> "AM").
 *
 * @param baseVehicleVersion - Aktualna wartość pola {{baseVehicleVersion}}
 * @returns Dwuznakowe oznaczenie (np. "AM", "7M") lub null, jeśli za krótkie/brak
 */
export function extractGearboxMarker(
  baseVehicleVersion: string | undefined | null,
): string | null {
  if (!baseVehicleVersion || baseVehicleVersion.length < 4) return null;
  return baseVehicleVersion.slice(2, 4).toUpperCase();
}

/**
 * Dla tonażu zaczynającego się od "8" (innego niż "82") tabela źródłowa koduje
 * dziesiątkę tonażu wieloznacznikiem "8X" - ta funkcja generuje dodatkowy
 * kandydujący klucz tonażu, do użycia przez getAxleMassLimit
 * (vehicle-axle-mass-limits.ts) jako fallback, gdy dokładny tonaż nie ma
 * dopasowania.
 *
 * @param tonnage - Kod tonażu (np. "72", "82", "80")
 * @returns Lista kandydujących kluczy tonażu do przeszukania, w kolejności priorytetu
 */
export function expandIvecoTonnageCandidates(tonnage: string): string[] {
  const candidates = [tonnage];
  if (tonnage[0] === "8" && tonnage !== "82") {
    candidates.push("8X");
  }
  return candidates;
}
